import React, { useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import styled, { keyframes } from 'styled-components';
import { FaUser, FaLock, FaShieldAlt } from 'react-icons/fa';

// --- ANIMASYONLAR ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(242, 113, 39, 0.2); }
  50% { box-shadow: 0 0 20px rgba(242, 113, 39, 0.4); }
  100% { box-shadow: 0 0 5px rgba(242, 113, 39, 0.2); }
`;

// --- STYLED COMPONENTS ---

const LoginContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: radial-gradient(circle at center, #1a1c20 0%, #090a0c 100%);
  font-family: 'Plus Jakarta Sans', sans-serif;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: #f27127;
    filter: blur(150px);
    opacity: 0.05;
    top: -50px;
    right: -50px;
  }
`;

const FormCard = styled.form`
  display: flex;
  flex-direction: column;
  background: rgba(31, 31, 32, 0.7);
  backdrop-filter: blur(10px);
  padding: 60px 45px;
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  max-width: 440px;
  width: 90%;
  animation: ${fadeIn} 0.8s ease-out;
  z-index: 1;
  box-sizing: border-box; /* Inputların sığması için kritik */
`;

const BrandHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  .icon-wrapper {
    background: linear-gradient(135deg, #f27127 0%, #ff9a44 100%); /* Turuncu logo */
    width: 60px;
    height: 60px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 10px 20px rgba(242, 113, 39, 0.3);
    color: white;
    font-size: 1.8rem;
  }

  h2 {
    color: #ffffff;
    font-size: 2.2rem;
    font-weight: 800;
    margin: 0;
    letter-spacing: -1.5px;
    background: linear-gradient(to bottom, #fff, #94a3b8);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    color: #64748b;
    font-size: 0.95rem;
    margin-top: 10px;
  }
`;

const InputGroup = styled.div`
  width: 100%;
  margin-bottom: 24px;
  position: relative;
  box-sizing: border-box; /* Inputların sığması için kritik */

  & svg {
    position: absolute;
    top: 50%;
    left: 18px;
    transform: translateY(-50%);
    color: #4b5563;
    transition: all 0.3s ease;
    font-size: 1.1rem;
    z-index: 2;
  }

  &:focus-within svg {
    color: #f27127; /* Fokus durumunda turuncu */
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 18px 20px 18px 52px;
  background: rgba(13, 14, 16, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  font-size: 1rem;
  color: #fff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box; /* Inputların sığması için kritik */

  &::placeholder { color: #4b5563; }

  &:focus {
    border-color: #f27127; /* Fokus durumunda turuncu */
    background: rgba(13, 14, 16, 0.9);
    outline: none;
    box-shadow: 0 0 0 4px rgba(242, 113, 39, 0.1); /* Turuncu gölge */
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #f27127 0%, #e85d04 100%); /* Turuncu buton */
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 10px;
  box-sizing: border-box;
  animation: ${props => props.loading ? glow : 'none'} 2s infinite; /* Turuncu parlama */

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(242, 113, 39, 0.4); /* Turuncu gölge */
    filter: brightness(1.1);
  }

  &:active { transform: translateY(0); }

  &:disabled {
    background: #2d3238;
    color: #64748b;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const FooterNote = styled.div`
  text-align: center;
  margin-top: 30px;
  font-size: 0.8rem;
  color: #334155;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

// --- LOGIC ---

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', { username, password });
      const { token } = response.data;
      localStorage.setItem('token', token);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.message || 'Kimlik doğrulanamadı.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <FormCard onSubmit={handleLogin}>
        <BrandHeader>
          <div className="icon-wrapper">
            <FaShieldAlt />
          </div>
          <h2>ANTIK</h2>
          <p>Yönetici Giriş Portalı</p>
        </BrandHeader>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            padding: '12px',
            borderRadius: '12px',
            fontSize: '0.85rem',
            textAlign: 'center',
            marginBottom: '20px',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            {error}
          </div>
        )}

        <InputGroup>
          <FaUser />
          <Input
            type="text"
            placeholder="Kullanıcı Adı"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </InputGroup>

        <InputGroup>
          <FaLock />
          <Input
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </InputGroup>

        <LoginButton type="submit" disabled={loading} loading={loading}>
          {loading ? 'Sistem Doğrulanıyor...' : 'Giriş Yap'}
        </LoginButton>

        <FooterNote>Secure Admin Access Only • 2026</FooterNote>
      </FormCard>
    </LoginContainer>
  );
};

export default Login;